<template>
  <div>
    <div class="page-header">
      <h2><span class="breadcrumb-level">会员管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">运营记录</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">打卡记录管理</span></h2>
      <el-tooltip content="6个月以前的归档数据" placement="top">
        <el-switch
          v-model="isArchived"
          active-text="查看归档"
          inactive-text="当前数据"
          @change="handleArchiveToggle"
        />
      </el-tooltip>
    </div>

    <el-form :inline="true" style="margin-bottom:16px">
      <el-form-item label="订单号">
        <el-input v-model="filterOrderNo" placeholder="搜索" style="width:140px" clearable />
      </el-form-item>
      <el-form-item label="日志类型">
        <el-select v-model="filterLogType" placeholder="全部" style="width:120px" clearable>
          <el-option label="入住" value="checkin" />
          <el-option label="退房" value="checkout" />
          <el-option label="日常上报" value="daily" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="fetchData">查询</el-button>
        <el-button @click="resetFilter">重置</el-button>
      </el-form-item>
    </el-form>

    <el-alert v-if="isArchived" title="当前为历史归档数据" type="info" :closable="false" style="margin-bottom:12px" />
    <el-alert v-if="!isArchived" title="6个月以前的数据请在右上角切换查看" type="info" :closable="false" style="margin-bottom:12px" />

    <el-table :data="records" stripe v-loading="loading" default-expand-all style="width:100%">
      <el-table-column label="日志类型" width="120">
        <template #default="{row}">
          <div style="display:flex;flex-direction:column;align-items:center;gap:2px">
            <el-tag :type="row.log_type==='daily'?'warning':row.log_type==='checkin'?'success':'info'" size="small">
              {{logLabel(row.log_type)}}
            </el-tag>
            <span style="font-size:10px;color:#999;line-height:1.2">{{projectLabel(row.order_type)}}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="关联订单" prop="order_no" width="180" />
      <el-table-column width="95">
        <template #default="{row}">
          <el-button size="small" type="primary" link @click="showOrderRecords(row)">打卡流水</el-button>
        </template>
      </el-table-column>
      <el-table-column label="打卡店员" width="120">
        <template #default="{row}">{{row.clerk?.name||row.clerk?.nickname||'-'}}</template>
      </el-table-column>
      <el-table-column label="健康/图片" min-width="140">
        <template #default="{row}">
          <div style="display:flex;flex-direction:column;gap:2px;font-size:12px;line-height:1.4">
            <span>精神：{{joinArr(row.mental_state)}} | 进食：{{joinArr(row.diet)}} | 排便：{{row.feces||'-'}}</span>
            <div v-if="formatArray(row.images)?.length" style="display:flex;gap:4px;margin-top:2px">
              <el-image
                v-for="(img, i) in formatArray(row.images).slice(0,3)"
                :key="i"
                :src="img"
                style="width:36px;height:36px;border-radius:4px;cursor:pointer"
                fit="cover"
                :preview-src-list="formatArray(row.images)"
                preview-teleported
              />
              <el-tag v-if="formatArray(row.images).length > 3" size="small">+{{formatArray(row.images).length-3}}</el-tag>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="打卡时间" width="160"><template #default="{row}">{{formatDateTime(row.created_at)}}</template></el-table-column>
      <el-table-column label="操作" width="70" fixed="right">
        <template #default="{row}">
          <el-button size="small" @click="showDetail(row)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="pagination.total > 0"
      v-model:current-page="pagination.current_page"
      v-model:page-size="pagination.per_page"
      :total="pagination.total"
      layout="prev, pager, next, sizes, total"
      small
      background
      style="margin-top:16px;justify-content:flex-end"
      @change="fetchData"
    />

    <el-dialog v-model="detailDialog" title="打卡详情" width="750px">
      <template v-if="detailData.id">
        <el-descriptions title="基础信息" :column="2" border>
          <el-descriptions-item label="项目类型">{{projectLabel(detailData.order_type)}}</el-descriptions-item>
          <el-descriptions-item label="日志类型">{{logLabel(detailData.log_type)}}</el-descriptions-item>
          <el-descriptions-item label="关联订单">{{detailData.order_no||'-'}}</el-descriptions-item>
          <el-descriptions-item label="打卡店员">{{detailData.clerk?.name||detailData.clerk?.nickname||'-'}}</el-descriptions-item>
          <el-descriptions-item label="宠物品类">{{detailData.pet_category||'-'}}</el-descriptions-item>
          <el-descriptions-item label="体重">{{detailData.weight||'-'}}</el-descriptions-item>
          <el-descriptions-item label="打卡时间" :span="2">{{formatDateTime(detailData.created_at)}}</el-descriptions-item>
        </el-descriptions>

        <el-divider />

        <el-descriptions title="健康状况" :column="2" border>
          <el-descriptions-item label="精神状态">{{joinArr(detailData.mental_state)}}</el-descriptions-item>
          <el-descriptions-item label="饮食">{{joinArr(detailData.diet)}}</el-descriptions-item>
          <el-descriptions-item label="饮水">{{formatArray(detailData.water)}}</el-descriptions-item>
          <el-descriptions-item label="粪便">{{detailData.feces||'-'}}</el-descriptions-item>
          <el-descriptions-item label="尿液">{{formatArray(detailData.urine)}}</el-descriptions-item>
        </el-descriptions>

        <el-divider />

        <el-descriptions title="身体检查" :column="2" border>
          <el-descriptions-item label="梳毛">{{formatArray(detailData.grooming)}}</el-descriptions-item>
          <el-descriptions-item label="耳道清洁">{{formatArray(detailData.ear_cleaning)}}</el-descriptions-item>
          <el-descriptions-item label="皮肤">{{formatArray(detailData.skin)}}</el-descriptions-item>
          <el-descriptions-item label="四肢">{{formatArray(detailData.limbs)}}</el-descriptions-item>
        </el-descriptions>

        <el-divider v-if="detailData.log_type==='checkin'" />

        <el-descriptions v-if="detailData.log_type==='checkin'" title="入住服务" :column="2" border>
          <el-descriptions-item label="清洁">{{detailData.cleaning ? '是' : '否'}}</el-descriptions-item>
          <el-descriptions-item label="消毒">{{detailData.disinfection ? '是' : '否'}}</el-descriptions-item>
        </el-descriptions>

        <el-divider v-if="formatArray(detailData.images)?.length" />

        <div v-if="formatArray(detailData.images)?.length" style="margin-top:12px">
          <h4 style="margin:0 0 8px">图片</h4>
          <div style="display:flex;flex-wrap:wrap;gap:8px">
            <el-image
              v-for="(img, i) in formatArray(detailData.images)"
              :key="i"
              :src="img"
              style="width:100px;height:100px;border-radius:4px"
              fit="cover"
              :preview-src-list="formatArray(detailData.images)"
              preview-teleported
            />
          </div>
        </div>
      </template>
      <el-empty v-else description="无数据" />
    </el-dialog>

    <!-- 订单打卡流水弹窗 -->
    <el-dialog v-model="orderRecordsDialog" title="打卡流水" width="600px">
      <el-timeline v-if="orderRecords.length > 0">
        <el-timeline-item
          v-for="(item, i) in orderRecords"
          :key="i"
          :timestamp="formatDateTime(item.created_at)"
          placement="top"
        >
          <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap">
            <el-tag size="small" :type="item.log_type==='daily'?'warning':item.log_type==='checkin'?'success':'info'">
              {{logLabel(item.log_type)}}
            </el-tag>
            <span style="font-size:13px;color:#666">店员：{{item.clerk?.name||item.clerk?.nickname||'-'}}</span>
            <span style="font-size:13px;color:#666">精神状态：{{joinArr(item.mental_state)}}</span>
            <span style="font-size:13px;color:#666">进食：{{joinArr(item.diet)}}</span>
          </div>
        </el-timeline-item>
      </el-timeline>
      <el-empty v-else description="暂无打卡记录" />
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import * as api from '../../api/userExtensions';

const records = ref([]);
const loading = ref(false);
const filterLogType = ref('');
const filterOrderNo = ref('');
const isArchived = ref(false);
const pagination = reactive({ current_page: 1, per_page: 20, total: 0, last_page: 1 });
const detailDialog = ref(false);
const detailData = ref({});
const orderRecordsDialog = ref(false);
const orderRecords = ref([]);

const showOrderRecords = async (row) => {
  try {
    const r = await api.getCheckinRecords({ order_no: row.order_no });
    orderRecords.value = (r.data || []).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  } catch { orderRecords.value = []; }
  orderRecordsDialog.value = true;
};

const projectLabel = (t) => ({ hotel: '宠境酒店', park: '宠境乐园', daycare: '日托' }[t] || t || '-');
const logLabel = (t) => ({ checkin: '入住', checkout: '退房', daily: '日常上报' }[t] || t || '-');

const fetchData = async () => {
  loading.value = true;
  try {
    const params = { page: pagination.current_page, per_page: pagination.per_page };
    if (filterLogType.value) params.log_type = filterLogType.value;
    if (filterOrderNo.value) params.order_no = filterOrderNo.value;
    if (isArchived.value) params.archived = '1';
    const r = await api.getCheckinRecords(params);
    records.value = r.data || [];
    pagination.current_page = r.pagination?.current_page || 1;
    pagination.total = r.pagination?.total || 0;
  } catch {} finally { loading.value = false; }
};

const resetFilter = () => {
  filterLogType.value = '';
  filterOrderNo.value = '';
  isArchived.value = false;
  fetchData();
};

const handleArchiveToggle = () => {
  pagination.current_page = 1;
  fetchData();
};

const showDetail = (row) => {
  detailData.value = row;
  detailDialog.value = true;
};

const formatDateTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const formatArray = (val) => {
  if (!val) return null;
  if (Array.isArray(val)) return val;
  try { return JSON.parse(val); } catch { return null; }
};
const joinArr = (val) => {
  const a = formatArray(val);
  if (!a) return '-';
  const items = Array.isArray(a) ? a : [a];
  return items.map(v => ({ normal: '正常', good: '良好', bad: '差', 'very bad': '很差', excellent: '优秀', soft: '软', hard: '硬', 'normal shape': '正常', watery: '水样' }[v] || v)).join('、');
};

onMounted(fetchData);
</script>

<style scoped>

</style>
